"use client"

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useState } from "react";
import { deploymentAvailaility } from "@/services/deployment.service";
import { Input } from "../ui/input";
import { z } from "zod";
import { DomainSchema } from "@/schemas/domain.schema";
import { upgradeDomain } from "@/services/upgrade.service";
import { UpgradeStatus } from "@/constants/status";
import { checkInputFlipBoolean } from "@/helpers/input.helper";

interface Props {
    domain: z.infer<typeof DomainSchema>,
    updateData: () => void
}

export default function UpgradeModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [upgradeStatus, setUpgradeStatus] = useState(UpgradeStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [upgradeVersion, setUpgradeVersion] = useState("");
    const [upgradeSubVersion, setUpgradeSubVersion] = useState("");
    const [isDisabled, setIsDisabled] = useState(true);

    async function initiateUpgrade(domainID: string){
        setUpgradeStatus(UpgradeStatus.onGoing);

        document.getElementById("initiationbox-" + domainID)?.setAttribute("hidden", "true");
        
        const authCheck = await deploymentAvailaility();
        if(authCheck === false){
            console.log("Auth Failed");
            setUpgradeStatus(UpgradeStatus.notAuthroized);
            return;
        }

        const formData = new FormData();
        formData.append('domainID', domainID);
        formData.append('parentVersion', upgradeVersion);
        formData.append('upgradeVersion', upgradeSubVersion !== "" ? upgradeSubVersion : upgradeVersion);
        formData.append('isSubVersion', (upgradeSubVersion !== "" ? 'true' : 'false'));

        let status = "";
        try {
            status = await upgradeDomain(formData);
        } catch (error) {
            console.log(error)
            setFailedMessage("Upgrade failed. Check console.log for more information.");
            setUpgradeStatus(UpgradeStatus.failed);
            return;
        }
        
        if(status != "Upgrade Complete"){
            console.log(status);
            setFailedMessage("Upgrade failed. Check console.log for more information.<br>Otherwise, here: ".concat(status));
            setUpgradeStatus(UpgradeStatus.failed);
            return;
        }

        handleSuccess();
        return;
    }

    const handleSuccess = () => {
        setUpgradeStatus(UpgradeStatus.completed);
        setTimeout(() => {
            setUpgradeStatus(UpgradeStatus.standBy);
            setIsDisabled(true);
            props.updateData();
            setOpen(false);
        }, 3000);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer" onClick={() => {
                    const upgradeVersion = (
                        document.getElementById("upgradeVersion-" + props.domain.ID) as HTMLInputElement
                    ).value;

                    let upgradeSubVersion = null;
                    if(document.getElementById("subVersion-" + props.domain.ID) !== null){
                        upgradeSubVersion = (
                            document.getElementById("subVersion-" + props.domain.ID) as HTMLInputElement
                        ).value;
                    }
                    setUpgradeVersion(upgradeVersion);
                    setUpgradeSubVersion(upgradeSubVersion ?? "");
                }}>Upgrade Domain</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Upgrade Domain</DialogTitle>
                <DialogDescription>
                    <span>You sure you want to upgrade to {upgradeSubVersion !== "" ? upgradeSubVersion : upgradeVersion} for: {props.domain.Name}?</span> 
                </DialogDescription>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {upgradeStatus != UpgradeStatus.onGoing && upgradeStatus != UpgradeStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4" id={"initiationbox-" + props.domain.ID}>
                                    <Input title="CONFIRM" placeholder="Enter CONFIRM" onChange={(e) => checkInputFlipBoolean(e.target.value, setIsDisabled)}/>
                                    <Button id={"initiationbutton-" + props.domain.ID} disabled={isDisabled} className="cursor-pointer mb-3" onClick={(_e) => initiateUpgrade(props.domain.ID)}>Upgrade Domain</Button>
                                </div>
                            )}
                            {upgradeStatus == UpgradeStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Upgrading...</p>
                                </div>
                            )}
                            {upgradeStatus == UpgradeStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Upgrade Complete! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {upgradeStatus == UpgradeStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}